import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaChartLine, FaUser, FaEnvelope, FaGraduationCap, FaBuilding, FaIdCard, FaClock, FaComments } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { useStudentAuth } from '../../../lib/AuthProvider';
import { getSupabase } from '../../../lib/supabaseClient';
import './Profile.css';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { student, loading: authLoading, signOut } = useStudentAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check session validity on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const supabase = getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.log('Session invalid in Profile, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
        
        // If session exists but no student data and not loading, wait a bit more
        if (!student && !authLoading) {
          console.log('No student data but session exists, waiting for AuthProvider to load...');
          setTimeout(() => {
            if (!student && !authLoading) {
              console.log('Still no student data after timeout, redirecting to login');
              navigate('/', { replace: true });
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session in Profile:', error);
      }
    };

    if (!authLoading) {
      checkSession();
    }
  }, [student, authLoading, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="student-home">
      <header className="top-nav">
        <div className="logo">
          <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
        </div>
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          aria-controls="mobile-sidebar"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(true)}
        >
          {'\u2630'}
        </button>
        <div className="nav-links">
          <button 
            onClick={() => navigate('/student/home')} 
            className={`nav-link ${isActive('/student/home') ? 'active' : ''}`}
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className={`nav-link ${isActive('/student/attendance') ? 'active' : ''}`}
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className={`nav-link ${isActive('/student/progress') ? 'active' : ''}`}
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className={`nav-link ${isActive('/student/feedback') ? 'active' : ''}`}
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className={`nav-link ${isActive('/student/profile') ? 'active' : ''}`}
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="mobile-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <button 
            onClick={() => navigate('/student/home')} 
            className="nav-link"
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className="nav-link"
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className="nav-link"
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </nav>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1>Profile</h1>
          <p>Your account information</p>
        </div>

        {authLoading ? (
          <div className="profile-loading">
            <p>Loading profile...</p>
          </div>
        ) : student ? (
          <div className="profile-container">
            <div className="profile-panel">
              <div className="panel-header">Personal Information</div>
              <div className="panel-content">
                <div className="profile-field">
                  <label className="profile-label">
                    <FaUser className="field-icon" />
                    FIRST NAME
                  </label>
                  <div className="profile-value">{student.first_name || 'N/A'}</div>
                </div>
                <div className="profile-field">
                  <label className="profile-label">
                    <FaUser className="field-icon" />
                    LAST NAME
                  </label>
                  <div className="profile-value">{student.last_name || 'N/A'}</div>
                </div>
                <div className="profile-field">
                  <label className="profile-label">
                    <FaIdCard className="field-icon" />
                    STUDENT ID
                  </label>
                  <div className="profile-value">{student.student_id || 'N/A'}</div>
                </div>
                <div className="profile-field">
                  <label className="profile-label">
                    <FaEnvelope className="field-icon" />
                    EMAIL
                  </label>
                  <div className="profile-value">{student.email || 'N/A'}</div>
                </div>
              </div>
            </div>

            <div className="profile-panel">
              <div className="panel-header">Academic Information</div>
              <div className="panel-content">
                <div className="profile-field">
                  <label className="profile-label">
                    <FaGraduationCap className="field-icon" />
                    COURSE
                  </label>
                  <div className="profile-value">{student.course_info || 'N/A'}</div>
                </div>
                <div className="profile-field">
                  <label className="profile-label">
                    <FaBuilding className="field-icon" />
                    DEPARTMENT
                  </label>
                  <div className="profile-value">{student.department || 'N/A'}</div>
                </div>
                <div className="profile-field">
                  <label className="profile-label">
                    <FaUser className="field-icon" />
                    SUPERVISOR
                  </label>
                  <div className="profile-value">{student.supervisor || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="profile-error">
            <p>Unable to load profile information. Please try again later.</p>
            <button 
              className="action-button" 
              onClick={() => navigate('/student/home')}
            >
              Return to Home
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Profile;

